import { Section, PageHero, SectionHeading, StatTile, CalloutCard, BulletList } from "@/components/marketing/MarketingUI";
import { Compass, Cube, ChartLineUp, FileText, ShieldCheck, GlobeStand } from "@phosphor-icons/react";
import useSEO from "@/lib/useSEO";

const VALUES = [
  {
    title: "Reproducibility over convenience",
    body: "A resource statement is a legal document. Every SRES output is snapshotted with its inputs, so any past run can be re-downloaded byte-for-byte.",
  },
  {
    title: "Geology-native design",
    body: "Every screen is designed alongside working resource geologists. If it isn't useful on a real project, it doesn't ship.",
  },
  {
    title: "Standards, not opinions",
    body: "SRES defers to JORC, NI 43-101 and SAMREC where they speak — and stays out of the way where they don't.",
  },
  {
    title: "Enterprise as default",
    body: "Multi-tenant auth, audit logs, share links and admin controls come as base capabilities. Not paid add-ons.",
  },
];

const LEADERSHIP = [
  {
    name: "Founding Geoscience Team",
    role: "20+ years across Fe, Au, Cu and REEs",
    body: "Our senior geologists have signed off resource statements across India, Africa and Australia. They are the ones who tell us — often bluntly — what to build next.",
  },
  {
    name: "Founding Engineering Team",
    role: "Ex-hyperscale platforms & geospatial R&D",
    body: "The platform is engineered by Python, GIS and 3D-graphics specialists who obsess over correctness and speed at scale.",
  },
  {
    name: "Reporting & Compliance",
    role: "Competent Persons + audit partners",
    body: "Our CPR templates and disclosure hooks are reviewed by external Competent Persons and audit partners across three jurisdictions.",
  },
];

export default function AboutUsPage() {
  useSEO({
    title: "About us · A modern studio for defensible resource work",
    description: "SRES is built by geologists and engineers who spent years moving CSVs between legacy desktop tools. We built the studio we wished existed.",
    keywords: "SRES about, Shanvi Resources, resource estimation team",
    path: "/about-us",
  });
  return (
    <div data-testid="about-us-page">
      <PageHero
        eyebrow="About us"
        title="A modern studio for defensible resource work."
        description="SRES is built by geologists and engineers who spent too much of their careers moving CSVs between legacy desktop tools. We built the studio we wished existed."
        testid="about-hero"
      />

      <Section className="py-20 md:py-28" testid="about-story">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.2fr] gap-12 items-start">
          <SectionHeading
            eyebrow="Our story"
            title="From spreadsheet fatigue to a browser-native studio."
          />
          <div className="space-y-4 text-slate-700 text-[15px] leading-relaxed">
            <p>
              SRES started as an internal toolkit at a working mine site — a set of Python scripts
              that finally made cross-validation, kriging and CPR assembly sit inside the same
              workflow. When neighbouring operations kept asking to borrow it, we productised the
              toolkit into a proper multi-tenant platform.
            </p>
            <p>
              Today, SRES is used by exploration teams, mine planners and Competent Persons who
              want a modern studio without giving up the defensibility of the legacy suites. The
              product is opinionated where it needs to be — and quietly configurable everywhere
              else.
            </p>
            <p>
              We are based in India, ship to five continents, and remain small enough that the
              engineer who wrote the kriging solver is the same person who answers your support
              ticket.
            </p>
          </div>
        </div>
      </Section>

      <Section className="py-16 md:py-20 bg-[#F7F8FB]" testid="about-stats">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile value="5+" label="Reporting standards" testid="about-stat-1" />
          <StatTile value="24×7" label="Cloud-native" testid="about-stat-2" />
          <StatTile value="1 day" label="Response SLA" testid="about-stat-3" />
          <StatTile value="3" label="Regions actively served" testid="about-stat-4" />
        </div>
      </Section>

      <Section className="py-20 md:py-28" testid="about-values">
        <SectionHeading
          eyebrow="What we value"
          title="Four principles that shape every product decision."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {VALUES.map((v, i) => (
            <div key={v.title} className="p-8 rounded-2xl border border-slate-200 bg-white" data-testid={`about-value-${i}`}>
              <div className="text-[12px] font-mono uppercase tracking-[0.24em] text-brand-blue">Principle {String(i + 1).padStart(2, "0")}</div>
              <div className="mt-3 font-heading font-bold text-slate-900 text-2xl">{v.title}</div>
              <p className="mt-3 text-slate-600 text-[15px] leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="py-20 md:py-28 bg-[#F7F8FB]" testid="about-leadership">
        <SectionHeading
          eyebrow="Team"
          title="Small enough to know your name. Deep enough to sign off your report."
          description="SRES is delivered by a compact, senior team spanning geology, geostatistics, platform engineering and compliance."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {LEADERSHIP.map((l, i) => (
            <div key={l.name} className="p-7 rounded-xl bg-white border border-slate-200" data-testid={`about-team-${i}`}>
              <div className="w-14 h-14 rounded-lg bg-brand-blue text-white flex items-center justify-center">
                {i === 0 ? <Compass size={22} weight="bold" /> : i === 1 ? <ShieldCheck size={22} weight="bold" /> : <FileText size={22} weight="bold" />}
              </div>
              <div className="mt-5 font-heading font-bold text-slate-900 text-lg">{l.name}</div>
              <div className="text-[12px] uppercase tracking-[0.22em] text-brand-blue mt-1">{l.role}</div>
              <p className="mt-3 text-slate-600 text-[14px] leading-relaxed">{l.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="py-16 md:py-24" testid="about-cta">
        <CalloutCard
          eyebrow="Work with us"
          title="Let's build the modern resource practice your team deserves."
          description="Speak to a senior geologist about scoping a first engagement — or start with a working session on your own data."
          cta="Get in touch"
          to="/contact"
          testid="about-cta-callout"
        />
      </Section>
    </div>
  );
}
